<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    statusCode?: number
    /** When true, clear Nuxt error state before navigating (used by error.vue). */
    clearOnNavigate?: boolean
  }>(),
  {
    statusCode: 404,
    clearOnNavigate: false,
  },
)

const { t } = useI18n()
const localePath = useLocalePath()

const is404 = computed(() => props.statusCode === 404)

function goHome() {
  if (props.clearOnNavigate) {
    clearError({ redirect: localePath('/') })
    return
  }
  navigateTo(localePath('/'))
}

function goBack() {
  if (props.clearOnNavigate) clearError()
  if (import.meta.client && window.history.length > 1) {
    window.history.back()
    return
  }
  navigateTo(localePath('/'))
}
</script>

<template>
  <section class="error-page" :aria-labelledby="is404 ? 'error-title-404' : 'error-title'">
    <div class="container error-page__inner">
      <p class="error-page__code" aria-hidden="true">{{ statusCode || 500 }}</p>
      <h1 :id="is404 ? 'error-title-404' : 'error-title'" class="error-page__title">
        {{ is404 ? t('error.title404') : t('error.titleGeneric') }}
      </h1>
      <p class="error-page__desc">
        {{ is404 ? t('error.desc404') : t('error.descGeneric') }}
      </p>
      <div class="error-page__actions">
        <button type="button" class="btn btn--cardinal" @click="goHome">
          {{ t('error.backHome') }}
        </button>
        <button type="button" class="btn btn--outline" @click="goBack">
          {{ t('error.backPrev') }}
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.error-page {
  min-height: calc(100vh - 140px - 280px);
  display: flex;
  align-items: center;
  padding: 80px 0 100px;
  background:
    radial-gradient(ellipse at 20% 0%, rgba(140, 21, 21, 0.06), transparent 50%),
    radial-gradient(ellipse at 80% 100%, rgba(0, 108, 184, 0.05), transparent 45%),
    var(--fog);
}

.error-page__inner {
  text-align: center;
  max-width: 640px;
}

.error-page__code {
  font-family: var(--font-serif);
  font-size: clamp(72px, 14vw, 120px);
  font-weight: 700;
  line-height: 1;
  color: var(--theme-blue);
  opacity: 0.9;
  letter-spacing: -0.02em;
  margin-bottom: 12px;
}

.error-page__title {
  font-family: var(--font-serif);
  font-size: clamp(28px, 4vw, 36px);
  font-weight: 700;
  color: var(--black);
  margin-bottom: 16px;
}

.error-page__desc {
  font-size: 18px;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 36px;
}

.error-page__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
}

@media (max-width: 640px) {
  .error-page {
    padding: 48px 0 72px;
    min-height: calc(100vh - 140px - 200px);
  }

  .error-page__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .error-page__actions .btn {
    width: 100%;
    text-align: center;
  }
}
</style>
